import {
    U2bColumnDefinition,
    U2bColumnDefinitionBrakePoint
} from '@core/components/layout/table/table.types';
import { isPlainObject } from '@shared/functions/is-plain-object';
import {
    resolveEffectiveColumnDefinitions,
    sortColumnDefinitionsByPosition,
} from '@core/components/layout/table/table-column-definitions.util';

export interface TableExportColumnDto {
    name: string;
    key: string;
    format?: string;
}

// Zero-width chars: Tolgee in-context tools wrap every translation in invisible U+200C/U+200D markers.
const INVISIBLE_HEADER_CHARACTERS = /[\u200B-\u200D\u2060\uFEFF]/g;

export function sanitizeExportColumnHeader(name: string): string {
    return name
        .replace(INVISIBLE_HEADER_CHARACTERS, '')
        .replace(/&shy;|&nbsp;|<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function resolveExportColumns(
    columnDefinitions: U2bColumnDefinition[],
    breakpoint: U2bColumnDefinitionBrakePoint = U2bColumnDefinitionBrakePoint.Desktop,
): TableExportColumnDto[] {
    return columnDefinitions
        .filter(col =>
            isPlainObject(col.isActive) &&
            col.isActive[breakpoint] &&
            !col.isAvatar &&
            col.exportKey !== null
        )
        .map(col => ({
            name: sanitizeExportColumnHeader(col.name),
            key: col.exportKey ?? col.filter?.property ?? col.property,
            ...(col.exportFormat ? {format: col.exportFormat} : {}),
        }));
}

export function resolveExportColumnsFromSettings(
    defaultColumnDefinitions: U2bColumnDefinition[],
    userColumnDefinitionSettings?: U2bColumnDefinition[] | null,
    breakpoint: U2bColumnDefinitionBrakePoint = U2bColumnDefinitionBrakePoint.Desktop,
): TableExportColumnDto[] {
    const effectiveColumns = resolveEffectiveColumnDefinitions(
        defaultColumnDefinitions,
        userColumnDefinitionSettings,
    );

    return resolveExportColumns(effectiveColumns, breakpoint);
}

export function resolveExportColumnsFromLiveDefinitions(
    columnDefinitions: U2bColumnDefinition[],
    breakpoint: U2bColumnDefinitionBrakePoint = U2bColumnDefinitionBrakePoint.Desktop,
): TableExportColumnDto[] {
    return resolveExportColumns(
        sortColumnDefinitionsByPosition(columnDefinitions, breakpoint),
        breakpoint,
    );
}

/** Keep only columns the table header actually renders, in the rendered order. */
export function filterColumnDefinitionsToDisplayedProperties(
    columnDefinitions: U2bColumnDefinition[],
    displayedProperties: readonly string[] | undefined,
): U2bColumnDefinition[] {
    if (!displayedProperties?.length) {
        return columnDefinitions;
    }

    const order = new Map(displayedProperties.map((property, index) => [property, index]));

    return columnDefinitions
        .filter(col => order.has(col.property))
        .sort((left, right) => (order.get(left.property) ?? 0) - (order.get(right.property) ?? 0));
}

/**
 * Export columns exactly as the table header renders them for the given breakpoint
 * (the rendered order can differ from position[breakpoint] outside Desktop).
 */
export function resolveExportColumnsFromDisplayedColumns(
    columnDefinitions: U2bColumnDefinition[],
    displayedProperties: readonly string[] | undefined,
    breakpoint: U2bColumnDefinitionBrakePoint = U2bColumnDefinitionBrakePoint.Desktop,
): TableExportColumnDto[] {
    if (!displayedProperties) {
        return resolveExportColumnsFromLiveDefinitions(columnDefinitions, breakpoint);
    }

    return resolveExportColumns(
        filterColumnDefinitionsToDisplayedProperties(columnDefinitions, displayedProperties),
        breakpoint,
    );
}
